import { ENVIRONMENT_URL, DATAVERSE_TABLES } from '../constants/dataverse';

const environmentUrl = ENVIRONMENT_URL;
// Environment URL
// const environmentUrl = "https://org487f0635.crm8.dynamics.com";dev
export async function saveSectionData(accessToken: string, records: any[]) {
  const apiVersion = "9.2";
  const tableName = DATAVERSE_TABLES.PQI_FRONTS;
  // const tableName = "cr3ea_pqi_fronts";
  const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

  const header = {
    Accept: "application/json",
    "Content-Type": "application/json; charset=utf-8",
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    Prefer: "return=representation",
    Authorization: `Bearer ${accessToken}`,
  };

  const results = [];
  for (let i = 0; i < records.length; i++) {
    const record = records[i];
    try {
      console.log(`[saveSectionData] Attempting to save record ${i + 1}/${records.length}:`, {
        evaluationType: record.cr3ea_evaluationtype,
        cycle: record.cr3ea_cycle,
        criteria: record.cr3ea_criteria,
        packNo: record.cr3ea_packno,
        category: record.cr3ea_category,
        qualitytourid: record.cr3ea_qualitytourid,
        productname: record.cr3ea_productname,
        executivename: record.cr3ea_executivename
      });
      
      // Validate required fields before sending
      if (!record.cr3ea_evaluationtype) {
        throw new Error('Missing required field: cr3ea_evaluationtype');
      }
      if (!record.cr3ea_cycle) {
        throw new Error('Missing required field: cr3ea_cycle');
      }
      if (!record.cr3ea_category) {
        throw new Error('Missing required field: cr3ea_category');
      }
      
      console.log(`[saveSectionData] Full record being sent:`, JSON.stringify(record, null, 2));
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: header,
        body: JSON.stringify(record),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`[saveSectionData] Server response error for record ${i + 1}:`, {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText,
          record: {
            evaluationType: record.cr3ea_evaluationtype,
            cycle: record.cr3ea_cycle,
            criteria: record.cr3ea_criteria
          }
        });
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      
      const responseData = await response.json();
      console.log(`[saveSectionData] Record ${i + 1} saved successfully:`, {
        id: responseData.cr3ea_prod_pqi_frontid,
        evaluationType: responseData.cr3ea_evaluationtype,
        cycle: responseData.cr3ea_cycle
      });
      results.push(responseData);
    } catch (error) {
      console.error(`[saveSectionData] Error creating record ${i + 1}:`, {
        error: error instanceof Error ? error.message : String(error),
        record: {
          evaluationType: record.cr3ea_evaluationtype,
          cycle: record.cr3ea_cycle,
          criteria: record.cr3ea_criteria,
          packNo: record.cr3ea_packno,
          category: record.cr3ea_category
        },
        fullRecord: JSON.stringify(record, null, 2)
      });
      results.push(null);
    }
  }
  return results;
}

// Interface for NetWeightEvaluation offline submission
interface NetWeightEvaluationOfflineSubmission {
  cycleNo: number;
  records?: any[]; // MC data for NetWeightMonitoringRecord table
  packRecords?: any[]; // Pack data for cr3ea_prod_pqi_fronts table (saveSectionData format)
  packs?: Array<{ packNo: number; netWeight: string }>;
  packDefects?: any;
  formData?: any;
  timestamp?: number;
  plantTourId?: string;
}

/**
 * Sync offline NetWeightEvaluation data to backend
 * This function processes offline submissions and saves pack records using saveSectionData
 */
export async function syncOfflineDataToBackend(
  offlineData: NetWeightEvaluationOfflineSubmission[],
  plantTourId: string,
  userName: string,
  selectedShift?: string | null
): Promise<{ success: boolean; message: string }> {
  try {
    console.log('=== SYNCING NET WEIGHT EVALUATION OFFLINE DATA TO BACKEND ===');
    console.log('Offline data to sync:', offlineData);
    console.log('Offline data length:', offlineData.length);
    console.log('Plant Tour ID:', plantTourId);
    console.log('User Name:', userName);
    console.log('Selected Shift:', selectedShift);

    if (!offlineData || offlineData.length === 0) {
      console.log('No offline data to sync');
      return { success: true, message: 'No offline data to sync' };
    }

    // Get access token
    const { getAccessToken } = await import('./getAccessToken');
    const tokenResult = await getAccessToken();
    const accessToken = tokenResult?.token;

    if (!accessToken) {
      console.error('No access token available for syncing offline data');
      return { 
        success: false, 
        message: 'Failed to get access token for syncing offline data' 
      };
    }

    let totalSynced = 0;
    let totalErrors = 0;
    const errors: string[] = [];

    // Process each offline submission
    for (const submission of offlineData) {
      try {
        const cycleNo = submission.cycleNo;
        console.log(`Processing offline submission for cycle ${cycleNo}:`, submission);

        // Use packRecords if available (preferred format), otherwise fall back to packs
        let recordsToSave: any[] = [];
        
        if (submission.packRecords && submission.packRecords.length > 0) {
          // Use packRecords directly (already in saveSectionData format)
          // Ensure all required fields are present
          recordsToSave = submission.packRecords.map((record: any) => {
            // Validate and ensure all required fields are present
            const formData = submission.formData || {};
            
            // Extract pack number from packno field if available
            const packNoMatch = record.cr3ea_packno?.match(/\d+/);
            const packNo = packNoMatch ? packNoMatch[0] : 'Unknown';
            
            // Build validated record - don't use spread to avoid undefined/null values
            const validatedRecord: any = {
              // Required fields - ensure they're set and not null/undefined
              cr3ea_evaluationtype: (record.cr3ea_evaluationtype && record.cr3ea_evaluationtype.trim()) || `Pack ${packNo}`,
              cr3ea_criteria: (record.cr3ea_criteria && record.cr3ea_criteria.trim()) || 'Okay',
              cr3ea_cycle: (record.cr3ea_cycle && record.cr3ea_cycle.trim()) || `Cycle-${cycleNo}`,
              cr3ea_title: (record.cr3ea_title && record.cr3ea_title.trim()) || 'QA_' + (() => {
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear();
                return `${day}/${month}/${year}`;
              })(),
              cr3ea_category: (record.cr3ea_category && record.cr3ea_category.trim()) || 'Net Weight',
              cr3ea_qualitytourid: (record.cr3ea_qualitytourid && record.cr3ea_qualitytourid.trim()) || plantTourId || submission.plantTourId || '',
              cr3ea_productname: (record.cr3ea_productname && record.cr3ea_productname.trim()) || (formData.product && formData.product.trim()) || '',
              cr3ea_executivename: (record.cr3ea_executivename && record.cr3ea_executivename.trim()) || (formData.executiveName && formData.executiveName.trim()) || '',
              cr3ea_standardntweight: (record.cr3ea_standardntweight != null && String(record.cr3ea_standardntweight).trim()) ? String(record.cr3ea_standardntweight).trim() : (formData.standardWeight != null && String(formData.standardWeight).trim()) ? String(formData.standardWeight).trim() : undefined,
              cr3ea_observedby: (record.cr3ea_observedby && record.cr3ea_observedby.trim()) || (userName && userName.trim()) || '',
              cr3ea_batchno: (record.cr3ea_batchno && record.cr3ea_batchno.trim()) || (formData.batchNo && formData.batchNo.trim()) || '',
              cr3ea_lineno: (record.cr3ea_lineno && record.cr3ea_lineno.trim()) || (formData.lineNo && formData.lineNo.trim()) || '',
              cr3ea_pkd: (record.cr3ea_pkd && record.cr3ea_pkd.trim()) || (formData.packaged && formData.packaged.trim()) || '',
              cr3ea_expiry: (record.cr3ea_expiry && record.cr3ea_expiry.trim()) || (formData.expiry && formData.expiry.trim()) || '',
              cr3ea_shift: (record.cr3ea_shift && record.cr3ea_shift.trim()) || selectedShift || (formData.shift && formData.shift.trim()) || sessionStorage.getItem("shiftValue") || "shift 1",
              cr3ea_tourstartdate: (record.cr3ea_tourstartdate && record.cr3ea_tourstartdate.trim()) || (() => {
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear();
                return `${day}/${month}/${year}`;
              })(),
              cr3ea_packno: (record.cr3ea_packno && record.cr3ea_packno.trim()) || `Pack ${packNo}`,
              cr3ea_netweight: (record.cr3ea_netweight && String(record.cr3ea_netweight).trim()) || '',
              cr3ea_platform: (record.cr3ea_platform && record.cr3ea_platform.trim()) || 'PWA'
            };
            
            // Add defect fields if criteria is "Not Okay"
            if (validatedRecord.cr3ea_criteria === "Not Okay") {
              validatedRecord.cr3ea_defectcategory = (record.cr3ea_defectcategory && record.cr3ea_defectcategory.trim()) || 'Category B';
              validatedRecord.cr3ea_defect = (record.cr3ea_defect && record.cr3ea_defect.trim()) || '';
              validatedRecord.cr3ea_defectremarks = (record.cr3ea_defectremarks && record.cr3ea_defectremarks.trim()) || '';
            }
            
            // Remove any undefined or null values to ensure clean data
            Object.keys(validatedRecord).forEach(key => {
              if (validatedRecord[key] === undefined || validatedRecord[key] === null) {
                validatedRecord[key] = '';
              }
            });
            
            return validatedRecord;
          });
          console.log(`Using packRecords for cycle ${cycleNo}: ${recordsToSave.length} records`);
          console.log(`Sample packRecord (first record):`, JSON.stringify(recordsToSave[0], null, 2));
          
          // Validate that we have required fields
          const invalidRecords = recordsToSave.filter(r => !r.cr3ea_evaluationtype || !r.cr3ea_cycle || !r.cr3ea_category);
          if (invalidRecords.length > 0) {
            console.error(`Found ${invalidRecords.length} invalid records for cycle ${cycleNo}:`, invalidRecords);
            throw new Error(`Invalid records found: missing required fields (evaluationtype, cycle, or category)`);
          }
        } else if (submission.packs && submission.packs.length > 0) {
          // Fallback: Build records from packs if packRecords not available
          console.log(`Building records from packs for cycle ${cycleNo}: ${submission.packs.length} packs`);
          const formData = submission.formData || {};
          const packDefects = submission.packDefects || {};
          
          recordsToSave = submission.packs.map((pack) => {
            const packNo = pack.packNo;
            const currentPackDefects = packDefects[packNo] || {};
            
            // Determine criteria based on defect presence
            let criteria: "Okay" | "Not Okay" = "Okay";
            if (currentPackDefects.category || currentPackDefects.defect || currentPackDefects.majorDefect) {
              criteria = "Not Okay";
            }
            
            const base = {
              cr3ea_evaluationtype: `Pack ${packNo}`,
              cr3ea_criteria: criteria,
              cr3ea_cycle: `Cycle-${cycleNo}`,
              cr3ea_title: 'QA_' + (() => {
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear();
                return `${day}/${month}/${year}`;
              })(),
              cr3ea_expiry: formData.expiry || '',
              cr3ea_shift: selectedShift || formData.shift || sessionStorage.getItem("shiftValue") || "shift 1",
              cr3ea_batchno: formData.batchNo || '',
              cr3ea_lineno: formData.lineNo || '',
              cr3ea_category: 'Net Weight',
              cr3ea_pkd: formData.packaged || '',
              cr3ea_tourstartdate: (() => {
                const now = new Date();
                const day = String(now.getDate()).padStart(2, '0');
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const year = now.getFullYear();
                return `${day}/${month}/${year}`;
              })(),
              cr3ea_productname: formData.product || '',
              cr3ea_executivename: formData.executiveName || '',
              cr3ea_standardntweight: (formData.standardWeight != null && String(formData.standardWeight).trim()) ? String(formData.standardWeight).trim() : undefined,
              cr3ea_observedby: userName || '',
              cr3ea_qualitytourid: plantTourId || submission.plantTourId || '',
              cr3ea_packno: `Pack ${packNo}`,
              cr3ea_netweight: pack.netWeight || '',
              cr3ea_platform: 'PWA'
            };
            
            if (criteria === "Not Okay") {
              return {
                ...base,
                cr3ea_defectcategory: currentPackDefects.category || 'Category B',
                cr3ea_defect: currentPackDefects.defect || '',
                cr3ea_defectremarks: currentPackDefects.majorDefect || ''
              };
            }
            return base;
          });
        } else {
          console.warn(`No packRecords or packs found for cycle ${cycleNo}, skipping`);
          continue;
        }

        if (recordsToSave.length === 0) {
          console.warn(`No records to save for cycle ${cycleNo}, skipping`);
          continue;
        }

        // Save records using saveSectionData
        console.log(`Saving ${recordsToSave.length} records for cycle ${cycleNo}...`);
        console.log(`First record being saved:`, JSON.stringify(recordsToSave[0], null, 2));
        
        const saveResults = await saveSectionData(accessToken, recordsToSave);
        
        // Check if all records were saved successfully
        const failedRecords = saveResults.filter(result => result === null);
        const successCount = saveResults.filter(result => result !== null).length;
        
        console.log(`Cycle ${cycleNo} save results: ${successCount} succeeded, ${failedRecords.length} failed`);
        
        if (failedRecords.length > 0) {
          console.error(`Failed records for cycle ${cycleNo}:`, failedRecords);
          // Log which records failed
          saveResults.forEach((result, index) => {
            if (result === null) {
              console.error(`Failed to save record ${index + 1} for cycle ${cycleNo}:`, recordsToSave[index]);
            }
          });
          throw new Error(`Failed to save ${failedRecords.length} out of ${recordsToSave.length} records for cycle ${cycleNo}`);
        }
        
        if (successCount === 0) {
          throw new Error(`No records were saved successfully for cycle ${cycleNo}`);
        }
        
        console.log(`Successfully synced ${successCount} records for cycle ${cycleNo}`);
        totalSynced += successCount;
        
        // Add a small delay between cycles to avoid rate limiting
        if (offlineData.indexOf(submission) < offlineData.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.error(`Failed to sync cycle ${submission.cycleNo}:`, error);
        totalErrors++;
        errors.push(`Cycle ${submission.cycleNo}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    const message = totalErrors === 0
      ? `Successfully synced ${totalSynced} records`
      : `Synced ${totalSynced} records, ${totalErrors} errors: ${errors.join('; ')}`;
    
    console.log('Sync completed:', message);
    
    return {
      success: totalErrors === 0,
      message
    };
  } catch (error) {
    console.error('Error syncing offline NetWeightEvaluation data:', error);
    return {
      success: false,
      message: `Failed to sync offline data: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
} 